import { RouteRecordRaw } from 'vue-router';
import { requireAuth, setDayjsTimezone } from '@/common/router/navigationGuard';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { VIEW_MODE } from '@/common/define/common.define';
import { extractAccessibleRoutes } from '@/common/auth/auth.utils';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { MaxgaugeDatabaseSubMenu } from '@/common/utils';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import {
  requireDomainTargetOrTutorial,
  requireInstances,
  validateDatabaseOverviewPath,
} from '@/common/router/authGuard';

const PREFIX_ORACLE = 'oracle_';

const oracleMenu: RouteRecordRaw[] = [
  {
    path: '/oracle/',
    name: `${PREFIX_ORACLE}Real Time Monitoring`,
    component: BaseViewport,
    children: [
      {
        path: 'multiView',
        name: `${PREFIX_ORACLE}Multi View`,
        component: () => import('@/oracle/views/multiView/MultiView.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [
      requireAuth,
      () => requireDomainTargetOrTutorial('database'),
      requireInstances,
      setDayjsTimezone,
      validateDatabaseOverviewPath,
    ],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD,
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.REALTIME,
    },
  },
  {
    path: '/oracle/',
    name: `${PREFIX_ORACLE}SQL Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'sqlSummary',
        name: `${PREFIX_ORACLE}SQL Summary`,
        component: () => import('@/oracle/views/sqlSummary/SqlSummary.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'topNAnalysis',
        name: `${PREFIX_ORACLE}Top N Analysis`,
        component: () => import('@/database/views/topNAnalysis/oracle/TopNAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'planAnalysis',
        name: `${PREFIX_ORACLE}Plan Analysis`,
        component: () => import('@/oracle/views/planAnalysis/PlanAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'searchSql',
        name: `${PREFIX_ORACLE}Search SQL`,
        component: () => import('@/oracle/views/searchSql/SearchSql.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database')],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ORACLE_PA,
        [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ORACLE_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/oracle/',
    name: `${PREFIX_ORACLE}Event Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'waitSummary',
        name: `${PREFIX_ORACLE}Wait Summary`,
        component: () => import('@/oracle/views/waitSummary/WaitSummary.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, requireInstances],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ORACLE_PA,
        [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ORACLE_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/oracle/',
    name: `${PREFIX_ORACLE}Session Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'searchSession',
        name: `${PREFIX_ORACLE}Search Session`,
        component: () => import('@/database/views/searchSession/oracle/SearchSession.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, requireInstances],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ORACLE_PA,
        [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ORACLE_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/oracle/',
    name: `${PREFIX_ORACLE}Performance Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'trendAnalysis',
        name: `${PREFIX_ORACLE}Trend Analysis`,
        component: () => import('@/database/views/trendAnalysis/oracle/TrendAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'parameterHistory',
        name: `${PREFIX_ORACLE}Parameter History`,
        component: () => import('@/database/views/parameterHistory/oracle/ParameterHistory.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'alertHistory',
        name: `${PREFIX_ORACLE}Alert History`,
        component: () => import('@/oracle/views/alertHistory/AlertHistory.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ORACLE_PA,
        [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ORACLE_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/oracle/',
    name: `${PREFIX_ORACLE}DataSpace Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'tablespace',
        name: `${PREFIX_ORACLE}Tablespace`,
        component: () => import('@/oracle/views/tablespace/Tablespace.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'undoTemp',
        name: `${PREFIX_ORACLE}Undo & Temp`,
        component: () => import('@/oracle/views/undoTemp/UndoTemp.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ORACLE_PA,
        [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ORACLE_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
];

const subMenuMapping = {
  instanceView: `${PREFIX_ORACLE}Real Time Monitoring`,
  sqlAnalysis: `${PREFIX_ORACLE}SQL Analysis`,
  eventAnalysis: `${PREFIX_ORACLE}Event Analysis`,
  sessionAnalysis: `${PREFIX_ORACLE}Session Analysis`,
  performanceAnalysis: `${PREFIX_ORACLE}Performance Analysis`,
  dataSpaceAnalysis: `${PREFIX_ORACLE}DataSpace Analysis`,
};

const getOracleNaviMenu = (subMenu?: MaxgaugeDatabaseSubMenu['oracle']) => {
  const filteredOracleMenu = extractAccessibleRoutes(oracleMenu);

  if (subMenu && subMenuMapping[subMenu]) {
    return filteredOracleMenu.filter((menu) => menu.name === subMenuMapping[subMenu]);
  }

  return filteredOracleMenu.filter((menu) => menu.name !== subMenuMapping.instanceView);
};

export { oracleMenu, getOracleNaviMenu, PREFIX_ORACLE };
